import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserPlus, Pencil, Trash2, Shield, Eye, Users } from "lucide-react";
import { toast } from "sonner";

import { API_URL as API } from "@/lib/api";

const ROLE_CONFIG = {
  admin: { label: "Admin", color: "bg-rose-100 text-rose-700 border-rose-200" },
  supervisor: { label: "Supervisor", color: "bg-amber-100 text-amber-700 border-amber-200" },
  viewer: { label: "Viewer", color: "bg-sky-100 text-sky-700 border-sky-200" },
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "viewer" });
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/users`, { withCredentials: true });
      setUsers(data.users || []);
    } catch (err) {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => {
    setEditingUser(null);
    setForm({ email: "", password: "", name: "", role: "viewer" });
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setForm({ email: u.email, password: "", name: u.name || "", role: u.role });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingUser) {
        const body = { email: form.email, name: form.name, role: form.role };
        if (form.password) body.password = form.password;
        await axios.put(`${API}/api/users/${editingUser._id}`, body, { withCredentials: true });
        toast.success("Usuario actualizado");
      } else {
        if (!form.password) { toast.error("La contrasena es requerida"); setSubmitting(false); return; }
        await axios.post(`${API}/api/users`, form, { withCredentials: true });
        toast.success("Usuario creado");
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al guardar usuario");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Eliminar a ${u.name || u.email}?`)) return;
    try {
      await axios.delete(`${API}/api/users/${u._id}`, { withCredentials: true });
      toast.success("Usuario eliminado");
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al eliminar");
    }
  };

  const getRoleIcon = (role) => {
    if (role === "admin") return <Shield className="w-3.5 h-3.5" />;
    if (role === "supervisor") return <Users className="w-3.5 h-3.5" />;
    return <Eye className="w-3.5 h-3.5" />;
  };

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground">Gestion de Usuarios</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{users.length} usuarios registrados</p>
        </div>
        <Button data-testid="create-user-button" onClick={openCreate} className="bg-[#FF9900] hover:bg-[#E68A00]">
          <UserPlus className="w-4 h-4 mr-2" />
          Nuevo Usuario
        </Button>
      </div>

      <Card className="border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted">
              <TableHead className="font-semibold">Nombre</TableHead>
              <TableHead className="font-semibold">Email</TableHead>
              <TableHead className="font-semibold">Rol</TableHead>
              <TableHead className="font-semibold">Creado</TableHead>
              <TableHead className="font-semibold text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Cargando...</TableCell></TableRow>
            ) : users.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Sin usuarios</TableCell></TableRow>
            ) : (
              users.map((u) => {
                const rc = ROLE_CONFIG[u.role] || ROLE_CONFIG.viewer;
                return (
                  <TableRow key={u._id} data-testid={`user-row-${u._id}`}>
                    <TableCell className="font-medium">{u.name || "-"}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${rc.color} gap-1`}>
                        {getRoleIcon(u.role)}
                        {rc.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString("es-BO") : "-"}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u._id}`}>
                        <Pencil className="w-4 h-4 text-muted-foreground" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(u)} data-testid={`delete-user-${u._id}`}>
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md" data-testid="user-modal">
          <DialogHeader>
            <DialogTitle>{editingUser ? "Editar Usuario" : "Nuevo Usuario"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Nombre</label>
              <Input
                data-testid="user-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nombre completo"
                required
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Email</label>
              <Input
                data-testid="user-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="correo@ejemplo.com"
                required
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                {editingUser ? "Nueva Contrasena (dejar vacio para mantener)" : "Contrasena"}
              </label>
              <Input
                data-testid="user-password-input"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editingUser ? "Sin cambios" : "Minimo 6 caracteres"}
                required={!editingUser}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Rol</label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin - Acceso total</SelectItem>
                  <SelectItem value="supervisor">Supervisor - Sin dashboards</SelectItem>
                  <SelectItem value="viewer">Viewer - Solo lectura</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} data-testid="user-save-button" className="bg-[#FF9900] hover:bg-[#E68A00]">
                {submitting ? "Guardando..." : (editingUser ? "Actualizar" : "Crear")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
